import { useEffect, useMemo, useState } from 'react';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Skeleton from 'antd/lib/skeleton';
import message from 'antd/lib/message';
import { submitTicket } from '../../api/tickets';
import IconBadge from './IconBadge';

const { TextArea } = Input;

function TicketRequestForm({ loading = false }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const selectedDeviceType = Form.useWatch('deviceType', form);

  const dependentDeviceField = useMemo(() => {
    if (selectedDeviceType === 'Printer') {
      return {
        name: 'location',
        label: 'Location *',
        placeholder: 'Enter printer location',
      };
    }

    if (selectedDeviceType === 'Phone') {
      return {
        name: 'location',
        label: 'Location *',
        placeholder: 'Enter phone location',
      };
    }

    if (selectedDeviceType === 'Other' || selectedDeviceType === 'Software / Application' || selectedDeviceType === 'Internet / Router') {
      return {
        name: 'specify',
        label: 'Specify *',
        placeholder: 'Specify details',
      };
    }

    return {
      name: 'deviceName',
      label: 'Device Name *',
      placeholder: 'LAPTOP-QW92K0',
    };
  }, [selectedDeviceType]);

  useEffect(() => {
    const dependentFields = ['deviceName', 'location', 'specify'];
    const nextValues = dependentFields.reduce((accumulator, fieldName) => {
      if (fieldName !== dependentDeviceField.name) accumulator[fieldName] = undefined;
      return accumulator;
    }, {});
    form.setFieldsValue(nextValues);
  }, [dependentDeviceField.name, form]);

  const buildTicketPayload = (values) => {
    const basePayload = {
      name: values.name,
      siteAddress: values.siteAddress,
      email: values.email,
      phone: values.phone,
      deviceType: values.deviceType,
      subject: values.subject,
      description: values.description,
    };

    if (values.deviceType === 'Computer') return { ...basePayload, deviceName: values.deviceName };
    if (values.deviceType === 'Printer' || values.deviceType === 'Phone') return { ...basePayload, location: values.location };
    return { ...basePayload, specify: values.specify };
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = buildTicketPayload(values);
      const response = await submitTicket(payload);
      if (response?.success) {
        message.success(response?.message || 'Your ticket has been submitted successfully!');
        form.resetFields();
      } else {
        message.info(response?.message || 'Ticket submitted.');
      }
    } catch {
      message.error('Failed to submit your ticket. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <article className="request-card request-card--skeleton" aria-label="Loading request form">
        <header className="request-card__header">
          <Skeleton active title={{ width: '42%' }} paragraph={{ rows: 1, width: ['68%'] }} />
        </header>
        <div className="request-form request-skeleton">
          <Skeleton active paragraph={{ rows: 7 }} />
          <div className="request-skeleton__button">
            <Skeleton.Button active block />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="request-card" aria-labelledby="request-title">
      <header className="request-card__header">
        <h1 id="request-title">Submit a Ticket</h1>
        <p>Please provide the details below to help us resolve your issue.</p>
      </header>

      <Form
        form={form}
        className="request-form"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
        initialValues={{ deviceType: 'Computer' }}
      >
        <div className="request-form__row request-form__row--two">
          <Form.Item
            label="Your Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Enter your name" />
          </Form.Item>

          <Form.Item
            label="Business/Site Name"
            name="siteAddress"
            rules={[{ required: true, message: 'Business/Site name is required' }]}
          >
            <Input placeholder="Provide business/site name" />
          </Form.Item>
        </div>

        <div className="request-form__row request-form__row--two">
          <Form.Item
            label="Your Email"
            name="email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input type="email" placeholder="name@company.com" />
          </Form.Item>
          <Form.Item
            label="Your Phone Number"
            name="phone"
            rules={[{ required: true, message: 'Phone number is required' }]}
          >
            <Input type="tel" placeholder="+61 412 345 678" />
          </Form.Item>
        </div>

        <div className="request-form__row request-form__row--two">
          <Form.Item
            label="Impacted Device Type"
            name="deviceType"
            rules={[{ required: true, message: 'Please chose the impacted device type' }]}
          >
            <Select
              suffixIcon={<IconBadge icon="chevron" className="select-wrap__icon" />}
              placeholder="Please chose the impacted device type"
              options={[
                { label: 'Computer', value: 'Computer' },
                { label: 'Printer', value: 'Printer' },
                { label: 'Phone', value: 'Phone' },
                { label: 'Software / Application', value: 'Software / Application' },
                { label: 'Internet / Router', value: 'Internet / Router' },
                { label: 'Other', value: 'Other' },
              ]}
            />
          </Form.Item>

          <Form.Item
            key={dependentDeviceField.name}
            label={dependentDeviceField.label}
            name={dependentDeviceField.name}
            rules={[
              { required: true, message: `Enter ${dependentDeviceField.label.replace(' *', '').toLowerCase()}` },
            ]}
          >
            <Input placeholder={dependentDeviceField.placeholder} />
          </Form.Item>
        </div>

        <Form.Item
          label="Subject"
          name="subject"
          rules={[{ required: true, message: 'Subject is required' }]}
        >
          <Input placeholder="Brief summary of the issue" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Description is required' }]}
        >
          <TextArea rows={5} placeholder="Please describe your problem in detail..." />
        </Form.Item>

        <div className="request-form__actions">
          <Button htmlType="submit" className="submit-button" type="primary" loading={submitting}>
            Submit Ticket
            <IconBadge icon="send" className="submit-button__icon" />
          </Button>
        </div>
      </Form>
    </article>
  );
}

export default TicketRequestForm;
